import { useCallback } from 'react'
import { useUrlState } from './useUrlState'
import {
  VodPublishState,
  VodOriginFilter,
  VodMatchLinkFilter,
  VodPlayableFilter,
  VodSortColumn,
} from '../types/vod'

/** Mirrors the API's SORTABLE_COLUMNS; anything else falls back. */
const SORT_COLUMNS: readonly VodSortColumn[] = [
  'display_date',
  'created_at',
  'title',
  'vod_type_code',
  'origin',
  'playable',
  'match_link',
  'publish_state',
]

export interface VodsFilters {
  vodTypeCode: string[]
  origin: VodOriginFilter
  publishState: VodPublishState[]
  dateFrom: string
  dateTo: string
  search: string
  matchId: string
  tags: string[]
  competitorIds: string[]
  seasonIds: string[]
  seriesSeasonId: string
  matchLink: VodMatchLinkFilter
  playable: VodPlayableFilter
  sortBy: VodSortColumn
  sortOrder: 'asc' | 'desc'
  page: number
}

/**
 * URL-backed filter state for the generalized VODs catalog (/vods).
 *
 * Separate from useVodFilters (the legacy highlights page) so the two can
 * evolve independently. Type is an open string because the option set comes
 * from the DB (GET /vod/types), not a hardcoded union.
 *
 * The multi-value keys stay singular (vodTypeCode, publishState) because the
 * key is the URL param: a link written before those filters went multi-select
 * still deserializes to a one-item list.
 */
export function useVodsFilters() {
  const defaultFilters: VodsFilters = {
    vodTypeCode: [],
    // Virtual assets are match placeholders with no media; the catalog hides
    // them unless asked for. '' (All Sources) stays reachable as ?origin=ALL.
    origin: 'NON_VIRTUAL',
    publishState: [],
    dateFrom: '',
    dateTo: '',
    search: '',
    matchId: '',
    tags: [],
    competitorIds: [],
    seasonIds: [],
    seriesSeasonId: '',
    matchLink: '',
    playable: '',
    // The date the list actually shows, newest first. Ordering by created_at
    // sorted by a column no reader can see.
    sortBy: 'display_date',
    sortOrder: 'desc',
    page: 1,
  }

  const { state, updateState } = useUrlState<VodsFilters>(defaultFilters, {
    vodTypeCode: {
      serialize: (value) => (value.length > 0 ? value.join(',') : undefined),
      deserialize: (value) => (value ? value.split(',').filter(Boolean) : []),
    },
    origin: {
      serialize: (value) =>
        value === 'NON_VIRTUAL' ? undefined : value === '' ? 'ALL' : value,
      deserialize: (value) =>
        value === 'ALL' ? '' : ((value as VodOriginFilter) ?? 'NON_VIRTUAL') || 'NON_VIRTUAL',
    },
    publishState: {
      serialize: (value) => (value.length > 0 ? value.join(',') : undefined),
      deserialize: (value) =>
        value ? (value.split(',').filter(Boolean) as VodPublishState[]) : [],
    },
    dateFrom: {
      serialize: (value) => (value ? value : undefined),
      deserialize: (value) => value || '',
    },
    dateTo: {
      serialize: (value) => (value ? value : undefined),
      deserialize: (value) => value || '',
    },
    search: {
      // Trimmed only to decide whether the param is worth writing, never in
      // the value itself: the input reads back from this state, so trimming
      // here deleted a trailing space as fast as it was typed — which reads
      // as the space bar not working in a multi-word search. The request
      // trims at the API boundary instead.
      serialize: (value) => (value.trim() ? value : undefined),
      deserialize: (value) => value || '',
    },
    matchId: {
      serialize: (value) => (value ? value : undefined),
      deserialize: (value) => value || '',
    },
    seriesSeasonId: {
      serialize: (value) => (value ? value : undefined),
      deserialize: (value) => value || '',
    },
    tags: {
      serialize: (value) => (value.length > 0 ? value.join(',') : undefined),
      deserialize: (value) => (value ? value.split(',').filter(Boolean) : []),
    },
    // Ids only in the URL; the pickers resolve them to names on load, so a
    // shared link still shows what it is filtering by.
    competitorIds: {
      serialize: (value) => (value.length > 0 ? value.join(',') : undefined),
      deserialize: (value) => (value ? value.split(',').filter(Boolean) : []),
    },
    seasonIds: {
      serialize: (value) => (value.length > 0 ? value.join(',') : undefined),
      deserialize: (value) => (value ? value.split(',').filter(Boolean) : []),
    },
    matchLink: {
      serialize: (value) => (value ? value : undefined),
      deserialize: (value) => (value as VodMatchLinkFilter) || '',
    },
    playable: {
      serialize: (value) => (value ? value : undefined),
      deserialize: (value) => (value as VodPlayableFilter) || '',
    },
    sortBy: {
      serialize: (value) => (value !== 'display_date' ? value : undefined),
      // Clamped, unlike the free-form filters: this is a closed set the API
      // rejects with a 400, so a stale or hand-edited ?sortBy would other-
      // wise replace the whole list with an error state.
      deserialize: (value) =>
        SORT_COLUMNS.includes(value as VodSortColumn)
          ? (value as VodSortColumn)
          : 'display_date',
    },
    sortOrder: {
      serialize: (value) => (value !== 'desc' ? value : undefined),
      deserialize: (value) => (value as 'asc' | 'desc') || 'desc',
    },
    page: {
      serialize: (value) => (value > 1 ? String(value) : undefined),
      deserialize: (value) => (value ? parseInt(value, 10) : 1),
    },
  })

  /** Update filters and reset to page 1 */
  const updateFilterAndResetPage = useCallback(
    (updates: Partial<VodsFilters>) => {
      updateState({ ...updates, page: 1 })
    },
    [updateState]
  )

  /** Change page and scroll to top */
  const handlePageChange = useCallback(
    (newPage: number) => {
      updateState({ page: newPage })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [updateState]
  )

  return {
    filters: state,
    updateFilters: updateState,
    updateFilterAndResetPage,
    handlePageChange,
  }
}
